'use client';

import { useRef } from 'react';
import { Loader2, UploadCloud } from 'lucide-react';
import type { AssetRecord, PrepareAssetUploadInput, UploadProgress } from '@impact/assets';
import { Button } from '@/components/ui/button/button';
import { cn } from '@/lib/utils';
import { useAssetUpload } from '../hooks/use-asset-upload';

interface AssetUploadButtonProps {
  assetType: PrepareAssetUploadInput['assetType'];
  ownerType?: PrepareAssetUploadInput['ownerType'];
  ownerId?: PrepareAssetUploadInput['ownerId'];
  visibility?: PrepareAssetUploadInput['visibility'];
  tags?: PrepareAssetUploadInput['tags'];
  source?: PrepareAssetUploadInput['source'];
  uploadedByType?: PrepareAssetUploadInput['uploadedByType'];
  uploadedById?: PrepareAssetUploadInput['uploadedById'];
  assetMetadata?: PrepareAssetUploadInput['assetMetadata'];
  accept?: string;
  disabled?: boolean;
  className?: string;
  buttonLabel?: string;
  onUploaded: (asset: AssetRecord) => void;
  onError?: (error: unknown) => void;
  onProgress?: (progress: UploadProgress) => void;
}

export function AssetUploadButton({
  assetType,
  ownerType,
  ownerId,
  visibility,
  tags,
  source,
  uploadedByType,
  uploadedById,
  assetMetadata,
  accept,
  disabled,
  className,
  buttonLabel = 'Upload Asset',
  onUploaded,
  onError,
  onProgress,
}: AssetUploadButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { upload, isUploading, progress } = useAssetUpload();

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={accept}
        disabled={disabled || isUploading}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (!file) return;

          void upload({
            file,
            assetType,
            ownerType,
            ownerId,
            visibility,
            tags,
            source,
            uploadedByType,
            uploadedById,
            assetMetadata,
            onProgress,
          })
            .then((result) => onUploaded(result.asset))
            .catch((error) => onError?.(error))
            .finally(() => {
              if (inputRef.current) {
                inputRef.current.value = '';
              }
            });
        }}
      />

      <Button
        type="button"
        variant="outline"
        className={cn('gap-2', className)}
        disabled={disabled || isUploading}
        onClick={() => inputRef.current?.click()}
      >
        {isUploading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Uploading{progress ? ` ${progress.percent}%` : ''}
          </>
        ) : (
          <>
            <UploadCloud className="h-4 w-4" />
            {buttonLabel}
          </>
        )}
      </Button>
    </>
  );
}
